import { useMemo } from 'react'
import { useLocation } from 'react-router'
import { ROUTE_TRADER } from '@klear/constants'
import { getAppBasePath } from '@klear/runtime-env'
import { trpc } from '@klear/ui/lib/trpc'
import { useMutationWithToast, createToastConfig } from '@klear/ui/hooks/use-mutations-with-toast'
import { useAuth } from '@klear/ui/contexts/auth-context'
import type { LegalAgreement } from '@klear/legal-agreement'
import { traderIdFromPathname } from '@/lib/trader-path'

function useTraderInPath(): boolean {
  const { pathname } = useLocation()
  return traderIdFromPathname(pathname, getAppBasePath(ROUTE_TRADER)) != null
}

function toAgreements(agreements: LegalAgreement[] | null | undefined): LegalAgreement[] {
  return agreements ?? []
}

export function useUserLegalAgreements() {
  const { user } = useAuth()
  const hasTrader = useTraderInPath()
  const { data, isLoading } = trpc.legalAgreement.getMissingUserLegalAgreements.useQuery(undefined, {
    enabled: !user?.hasKlearRole && hasTrader
  })

  const missingUserLegalAgreements = useMemo(() => toAgreements(data), [data])

  return {
    missingUserLegalAgreements,
    isLoading,
    missingUserPolicies: missingUserLegalAgreements
  }
}

export function useUnsignedAchAccounts() {
  const { user } = useAuth()
  const hasTrader = useTraderInPath()
  const { data, isLoading } = trpc.legalAgreement.getUnsignedAchAccounts.useQuery(undefined, {
    enabled: !!user && !user.hasKlearRole && hasTrader
  })

  const achAgreement = data?.achAgreement ?? null
  const unsignedExternalAccounts = data?.unsignedExternalAccounts ?? []
  const unsignedKlearAccounts = data?.unsignedKlearAccounts ?? []
  const hasUnsignedAccounts = unsignedExternalAccounts.length > 0 || unsignedKlearAccounts.length > 0

  return {
    achAgreement,
    unsignedExternalAccounts,
    unsignedKlearAccounts,
    hasUnsignedAccounts,
    isLoading
  }
}

export function useTraderLegalAgreements() {
  const { user } = useAuth()
  const utils = trpc.useUtils()

  const { data, isLoading } = trpc.legalAgreement.getMissingTraderLegalAgreements.useQuery(undefined, {
    enabled: !user?.hasKlearRole
  })

  const missingTraderLegalAgreements = useMemo(() => toAgreements(data), [data])

  const invalidateTraderLegalAgreements = async () => {
    await utils.legalAgreement.getMissingTraderLegalAgreements.invalidate()
  }

  const fetchTraderLegalAgreements = async () => {
    return utils.legalAgreement.getMissingTraderLegalAgreements.fetch()
  }

  return {
    missingTraderLegalAgreements,
    isLoading,
    invalidateTraderLegalAgreements,
    fetchTraderLegalAgreements
  }
}

export function useLegalAgreementAcceptanceMutations() {
  const invalidateQueries = [
    'legalAgreement.getMissingUserLegalAgreements',
    'legalAgreement.getMissingTraderLegalAgreements',
    'legalAgreement.getUnsignedAchAccounts'
  ]
  const createTraderLegalAgreementAcceptanceMutation = useMutationWithToast(
    trpc.legalAgreement.createTraderLegalAgreementAcceptance.useMutation,
    createToastConfig('Agreement', 'create'),
    {
      invalidateQueries
    }
  )

  const createUserLegalAgreementAcceptanceMutation = useMutationWithToast(
    trpc.legalAgreement.createUserLegalAgreementAcceptance.useMutation,
    createToastConfig('Agreement', 'create'),
    {
      invalidateQueries
    }
  )

  const createTraderLegalAgreementAcceptance = async (input: {
    legalAgreementId: number
    signingSessionId: number
    generatedDocumentId?: number
    signature?: string
    signerData?: {
      firstName: string
      lastName: string
      email: string
      title: string
      [key: string]: unknown
    }
    signaturePdfUrl?: string
    consentText?: string
    klearAccountIds?: number[]
    externalAccountIds?: number[]
  }) => {
    const result = await createTraderLegalAgreementAcceptanceMutation.mutateAsync(input)
    return result
  }

  const createUserLegalAgreementAcceptance = async (
    legalAgreementId: number,
    signature?: string,
    consentText?: string
  ) => {
    const result = await createUserLegalAgreementAcceptanceMutation.mutateAsync({
      legalAgreementId,
      signature,
      consentText
    })
    return result
  }
  return {
    createTraderLegalAgreementAcceptance: createTraderLegalAgreementAcceptance,
    isCreatingTraderLegalAgreementAcceptance: createTraderLegalAgreementAcceptanceMutation.isPending,
    traderLegalAgreementAcceptanceError: createTraderLegalAgreementAcceptanceMutation.error,
    createUserLegalAgreementAcceptance: createUserLegalAgreementAcceptance,
    isCreatingUserLegalAgreementAcceptance: createUserLegalAgreementAcceptanceMutation.isPending,
    userLegalAgreementAcceptanceError: createUserLegalAgreementAcceptanceMutation.error,
    createTraderPolicyAcceptance: createTraderLegalAgreementAcceptance,
    isCreatingTraderPolicyAcceptance: createTraderLegalAgreementAcceptanceMutation.isPending,
    traderPolicyAcceptanceError: createTraderLegalAgreementAcceptanceMutation.error,
    createUserPolicyAcceptance: createUserLegalAgreementAcceptance,
    isCreatingUserPolicyAcceptance: createUserLegalAgreementAcceptanceMutation.isPending,
    userPolicyAcceptanceError: createUserLegalAgreementAcceptanceMutation.error
  }
}

export function useGeneratedLegalAgreementMutations() {
  const invalidateQueries = [
    'legalAgreement.getMissingUserLegalAgreements',
    'legalAgreement.getMissingTraderLegalAgreements',
    'legalAgreement.getUnsignedAchAccounts'
  ]

  const generateDocumentMutation = useMutationWithToast(
    trpc.legalAgreement.generateDynamicDocument.useMutation,
    createToastConfig(null),
    {
      invalidateQueries
    }
  )

  const signDocumentMutation = useMutationWithToast(
    trpc.legalAgreement.signDocument.useMutation,
    createToastConfig(null),
    {
      invalidateQueries
    }
  )

  const uploadSignedPdfAndCreateAcceptance = async (params: {
    traderId: number
    documentHash: string
    legalAgreementId: number
    signature: string
    signerData: {
      firstName: string
      lastName: string
      email: string
      title: string
      [key: string]: unknown
    }
    documentContentUrl: string
    documentPresentedAt: string
    consentText?: string
    userAgent?: string
    signingSessionId: number
    klearAccountIds?: number[]
    externalAccountIds?: number[]
  }) => {
    const result = await signDocumentMutation.mutateAsync({
      documentHash: params.documentHash,
      signature: params.signature,
      signerData: params.signerData,
      documentContentUrl: params.documentContentUrl,
      createAcceptance: true,
      legalAgreementId: params.legalAgreementId,
      documentPresentedAt: params.documentPresentedAt,
      signingSessionId: params.signingSessionId,
      ...(params.consentText && { consentText: params.consentText }),
      ...(params.klearAccountIds && params.klearAccountIds.length > 0 && { klearAccountIds: params.klearAccountIds }),
      ...(params.externalAccountIds &&
        params.externalAccountIds.length > 0 && { externalAccountIds: params.externalAccountIds })
    })

    return result
  }

  const recordSigningSessionEventMutation = trpc.legalAgreement.recordSigningSessionEvent.useMutation()

  const recordSigningSessionEvent = async (params: {
    signingSessionId?: number | null
    legalAgreementId: number
    eventType: string
    payload?: Record<string, unknown>
  }) => {
    return recordSigningSessionEventMutation.mutateAsync({
      ...params,
      signingSessionId: params.signingSessionId ?? undefined
    })
  }

  return {
    generateDocument: generateDocumentMutation.mutateAsync,
    isGeneratingDocument: generateDocumentMutation.isPending,
    generateDocumentError: generateDocumentMutation.error,
    uploadSignedPdfAndCreateAcceptance,
    recordSigningSessionEvent
  }
}

export const useLegalAgreements = useUserLegalAgreements
